/**
 * IconSelect
 * @module components/IconSelect
 */

import React, { useState, useEffect, useContext } from 'react';
import { LocalizationContext } from 'context/localization.context';
import { useToggle } from 'hooks';
import Tooltip from '../Tooltip';
import { LinkModel } from 'types/common';
import clsx from 'clsx';
import Icon, { iconTypes } from '../Icon/Icon';
import Button from '../Button';

interface IconSelectmodel {
	/** Items  */
	items: any[];

	/** Icon next to IconSelect text   */
	icon: iconTypes;

	/**optional icon color */
	iconColor?: string;

	/** Id for IconSelect */
	id: string;

	/** Selected item */
	selected: any;

	/** Callback function */
	onChange: (item: LinkModel) => void;

	/** Optional tooltip for warehouse select */
	warehouseTooltip?: string;

	loading?: boolean;

	salesUser?: boolean;

	order?: string;

	handleOpenCustomerSearch?: () => void;
}

const IconSelect = React.memo(
	({
		items,
		icon,
		id,
		order,
		selected,
		onChange,
		warehouseTooltip,
		loading,
		salesUser,
		handleOpenCustomerSearch,
		iconColor = '#0075BF',
	}: IconSelectmodel) => {
		const { t }: any = useContext(LocalizationContext);
		const [expanded, toggle, ref] = useToggle(false, true);
		const [selectedItem, setSelectedItem] = useState<any>(selected);
		const selectedItemLabel = selectedItem?.name;

		useEffect(() => {
			setSelectedItem(selected);
		}, [selected]);

		const handleItemClick = (e: React.MouseEvent, item: LinkModel) => {
			if (item.name) {
				e.preventDefault();
			}

			if (onChange) {
				onChange(item);
			}

			setSelectedItem(item);
			toggle();
		};

		return (
			<div
				ref={ref}
				className={clsx(
					'relative border-t border-greyDark lg:border-transparent',
					order === '4' && 'order-4'
				)}
			>
				<button
					aria-controls={id}
					aria-expanded={expanded}
					onClick={(e) => {
						e.preventDefault();
						!loading && toggle();
					}}
					title={
						loading
							? `${t('shared/header/loading')}...`
							: id === 'customers'
							? `${selectedItem.name} - ${selectedItem.externId}`
							: `${selectedItem.name}`
					}
					className={clsx(
						'flex my-2 items-center justify-between w-full w- xl:justify-start',
						loading && 'opacity-50 cursor-not-allowed'
					)}
				>
					<span
						className={clsx(
							'flex items-center gap-3 xl:gap-1 justify-between xl:justify-center lg:max-w-50 xl:max-w-30 text-p text-black ml-2'
						)}
					>
						<span>
							<Icon
								icon={icon}
								size={1.25}
								color={iconColor}
								className="-mt-1"
							/>
						</span>
						<span className="lg:truncate max-w-full lg:max-w-50 xl:max-w-30 text-left ">
							{loading ? `${t('shared/header/loading')}...` : selectedItemLabel}
						</span>
						{warehouseTooltip && (
							<span onClick={(e) => e.stopPropagation()}>
								<Tooltip title={t('menuoptions/warehousehelp')}>
									<p className="text-xsm m-0">{warehouseTooltip}</p>
								</Tooltip>
							</span>
						)}
					</span>
					<span>
						<Icon
							icon="chevron"
							size={0.75}
							direction={expanded ? 'down' : 'up'}
							color={iconColor}
							className={clsx(warehouseTooltip ? 'ml-0' : 'ml-3')}
						/>
					</span>
				</button>

				<div
					className={clsx(
						items && items.length > 0 && expanded ? 'block' : 'hidden',
						'min-w-full lg:absolute lg:right-0 lg:left-auto left-0 bg-white z-100 xl:px-3 px-0 py-1 xl:shadow-md rounded-2xl'
					)}
					id={id}
					aria-hidden={!expanded}
					tabIndex={expanded ? -1 : undefined}
				>
					{items && (
						<ul className="list-none m-0 p-0 overflow-y-auto max-h-113">
							{items.map((item, i) => {
								return (
									<li className="mr-6 whitespace-nowrap" key={i}>
										<abbr className="no-underline" title={item.name}>
											<a
												title={
													id === 'customers'
														? `${item.name} - ${item.externId}`
														: item.name
												}
												className="whitespace-normal hover:underline text-p text-black sm:truncate max-w-65 block"
												href="#"
												onClick={(e) => handleItemClick(e, item)}
											>
												{item.name}
												{id === 'customers' && (
													<>
														<br />
														<p className="font-standard text-p">
															{item?.externId}
														</p>
													</>
												)}
											</a>
										</abbr>
									</li>
								);
							})}
						</ul>
					)}
					{salesUser && id !== 'types' && (
						<Button
							buttonColor="blue"
							onClick={handleOpenCustomerSearch}
							className="w-full"
						>
							{t('shared/header/switchcustomer')}
						</Button>
					)}
				</div>
			</div>
		);
	}
);

export default IconSelect;
